import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, TrashSimple, PencilSimple, MapPin, Camera } from "@phosphor-icons/react";
import StoreMap from "../components/StoreMap";

const empty = {
  employee_id: "",
  date: new Date().toISOString().slice(0, 10),
  location_type: "offsite",
  name: "",
  address: "",
  latitude: 0,
  longitude: 0,
  radius_meters: 100,
  reference_number: "",
  note: "",
  requires_photo: false,
};

export default function Assignments() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const [a, u] = await Promise.all([
        api.get("/assignments"),
        api.get("/users"),
      ]);
      setItems(a.data);
      setEmployees(u.data.filter((x) => x.role === "employee"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius_meters: parseInt(form.radius_meters, 10),
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editingId) {
        delete payload.employee_id;
        delete payload.date;
        delete payload.location_type;
        await api.patch(`/assignments/${editingId}`, payload);
        toast.success("Assignment updated");
      } else {
        await api.post("/assignments", payload);
        toast.success("Assignment created");
      }
      setForm(empty);
      setEditingId(null);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setForm((f) => ({ ...f, latitude: p.coords.latitude, longitude: p.coords.longitude })),
      (err) => toast.error(err.message)
    );
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      employee_id: a.employee_id, date: a.date,
      location_type: a.location_type || "offsite",
      name: a.name, address: a.address,
      latitude: a.latitude, longitude: a.longitude,
      radius_meters: a.radius_meters,
      reference_number: a.reference_number || "",
      note: a.note || "",
      requires_photo: !!a.requires_photo,
    });
    setShowForm(true);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete assignment "${a.name}"?`)) return;
    try {
      await api.delete(`/assignments/${a.id}`);
      toast.success("Assignment deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((a) => a.date >= today);
  const past = items.filter((a) => a.date < today);

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="assignments-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Field work</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Off-site Assignments</h1>
          <p className="text-zinc-600 mt-1 text-sm">
            Send an employee to a delivery, meeting, or client visit — they'll only be allowed to clock in at that location.
          </p>
        </div>
        <button
          data-testid="new-assignment-btn"
          onClick={() => { setEditingId(null); setForm(empty); setShowForm((v) => !v); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm"
        >
          <Plus size={14} weight="bold" />
          New Assignment
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 grid lg:grid-cols-2 gap-6" data-testid="assignment-form">
          <div className="space-y-4">
            <div className="label-eyebrow">{editingId ? "Edit assignment" : "New off-site assignment"}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Employee">
                <select required disabled={!!editingId} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="lom-input" data-testid="assignment-employee-select">
                  <option value="">— pick employee —</option>
                  {employees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              <Field label="Date">
                <input type="date" required disabled={!!editingId} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="lom-input" data-testid="assignment-date-input" />
              </Field>
            </div>
            <Field label="Assignment name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lom-input" placeholder="e.g. Client meeting at Acme HQ" data-testid="assignment-name-input" />
            </Field>
            <Field label="Address">
              <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="lom-input" placeholder="123 Main St, Anytown" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Latitude">
                <input type="number" step="any" required value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="lom-input font-mono" />
              </Field>
              <Field label="Longitude">
                <input type="number" step="any" required value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="lom-input font-mono" />
              </Field>
            </div>
            <button type="button" onClick={useMyLocation} className="flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-sm text-sm hover:bg-zinc-50">
              <MapPin size={14} weight="bold" />
              Use my current location
            </button>
            <Field label="Radius (meters)">
              <input type="number" min={10} required value={form.radius_meters} onChange={(e) => setForm({ ...form, radius_meters: e.target.value })} className="lom-input" data-testid="assignment-radius-input" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Reference number (optional)">
                <input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="lom-input font-mono" placeholder="e.g. JOB-2024-812" />
              </Field>
              <Field label="Note (optional)">
                <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="lom-input" />
              </Field>
            </div>
            <div className="flex items-center gap-2">
              <input id="req-photo" type="checkbox" checked={form.requires_photo} onChange={(e) => setForm({ ...form, requires_photo: e.target.checked })} data-testid="assignment-requires-photo" />
              <label htmlFor="req-photo" className="text-sm flex items-center gap-1"><Camera size={14} weight="bold" /> Require photo at clock-in</label>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-zinc-100">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border border-zinc-300 rounded-sm text-sm">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="assignment-submit-btn">
                {editingId ? "Save changes" : "Create assignment"}
              </button>
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Pick location on map</div>
            <StoreMap
              latitude={form.latitude}
              longitude={form.longitude}
              radiusMeters={parseInt(form.radius_meters, 10) || 0}
              onPick={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
              height={420}
            />
          </div>
        </form>
      )}

      {[["Upcoming & today", upcoming], ["Past", past]].map(([title, list]) =>
        list.length > 0 && (
          <div key={title} className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <h3 className="font-heading font-bold text-lg text-zinc-950">{title}</h3>
              <div className="text-xs font-mono text-zinc-500">{list.length}</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr><Th>Date</Th><Th>Employee</Th><Th>Assignment</Th><Th>Reference</Th><Th>Radius</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {list.map((a) => (
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                    <Td className="font-mono">{a.date}</Td>
                    <Td className="font-semibold">{a.employee_name}</Td>
                    <Td>
                      <div>{a.name}</div>
                      <div className="text-xs text-zinc-500">{a.address}</div>
                      {a.requires_photo && <div className="text-xs text-amber-700 mt-0.5"><Camera size={10} weight="bold" className="inline" /> photo required</div>}
                    </Td>
                    <Td className="font-mono text-xs">{a.reference_number || "—"}</Td>
                    <Td className="font-mono">{a.radius_meters}m</Td>
                    <Td className="text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(a)} className="p-1.5 hover:bg-zinc-100 rounded-sm"><PencilSimple size={14} /></button>
                        <button onClick={() => remove(a)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm"><TrashSimple size={14} /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {items.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center text-zinc-500">
          No assignments yet. Create one to send an employee off-site.
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="label-eyebrow block mb-1.5">{label}</label>{children}</div>
);
const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 ${className}`}>{children}</td>
);
